import React, { useEffect, useState } from 'react';
import {
  Zap,
  Wrench,
  Globe,
  Receipt,
  Briefcase,
  TrendingUp,
  Rocket,
  Banknote
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styles from './OnePagerStyles.module.css';
import logo from '../assets/logo.svg';
import SimpleModelViewer from './SimpleModelViewer';
import VideoPlayer from './VideoPlayer';
import LanguageSwitcher from './LanguageSwitcher';
import FloatingFAQButton from './FloatingFAQButton';
import FAQModal from './FAQModal';

const OnePager = () => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [isFAQModalOpen, setIsFAQModalOpen] = useState(false);

  useEffect(() => {
    // Check if mobile on mount and window resize
    const checkMobile = () => {
      setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ));
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Preload video for better performance
  useEffect(() => {
    const preloadVideo = async () => {
      try {
        console.log('OnePager: Starting video preload for waterflow.mp4');
        const response = await fetch('/videos/waterflow.mp4', {
          method: 'HEAD',
          headers: {
            'Accept': 'video/mp4'
          }
        });
        
        if (response.ok) {
          console.log('OnePager: Video preload successful, file size:', response.headers.get('content-length'));
          setVideoLoading(false);
        } else {
          console.warn('OnePager: Video preload failed with status:', response.status);
          setVideoLoading(false);
        }
      } catch (error) {
        console.error('OnePager: Video preload error:', error);
        setVideoLoading(false);
      }
    };
    
    preloadVideo();
  }, []);

  return (
    <div className={styles.onePager}>
      {/* Fixed top-right row for language switcher and FAQ button */}
      <div className={styles.onePagerTopRightControls}>
        <LanguageSwitcher className={styles.onePagerLanguageSwitcher} />
        <FloatingFAQButton className={styles.onePagerFAQButton} onClick={() => setIsFAQModalOpen(true)} />
      </div>
      <header className={styles.header}>
        <img src={logo} alt="Sylents Logo" className={styles.logo} style={{ width: '320px', height: 'auto' }} />
        <h1 className={styles.gradientHeadline}>{t('onePager.headline')}</h1>
        <p className={styles.subtitle}>
          {t('onePager.subheadline')}
        </p>
      </header>

      <section className={styles.modelSection}>
        <h2 className={styles.modelTitle}>{t('joinus_modelShowcaseTitle')}</h2>
        <div className={styles.modelContainer}>
          <SimpleModelViewer modelPath="/models/sy3_kapsel.glb" />
        </div>
        <div className={styles.modelCaption}>
          <div className={styles.modelSpecs}>
            <div className={styles.modelSpec}>
              <span className={styles.modelSpecLabel}>{t('modelShowcaseFeature1Title')}</span>
              <span className={styles.modelSpecValue}>{t('modelShowcaseFeature1Desc')}</span>
            </div>
            <div className={styles.modelSpec}>
              <span className={styles.modelSpecLabel}>{t('modelShowcaseFeature2Title')}</span>
              <span className={styles.modelSpecValue}>{t('modelShowcaseFeature2Desc')}</span>
            </div>
            <div className={styles.modelSpec}>
              <span className={styles.modelSpecLabel}>{t('modelShowcaseFeature3Title')}</span>
              <span className={styles.modelSpecValue}>{t('modelShowcaseFeature3Desc')}</span>
            </div>
            <div className={styles.modelSpec}>
              <span className={styles.modelSpecLabel}>{t('modelShowcaseFeature4Title')}</span>
              <span className={styles.modelSpecValue}>{t('modelShowcaseFeature4Desc')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.videoSection}>
        <div style={{ 
          position: 'relative', 
          width: '100%', 
          marginTop: '1rem', 
          borderRadius: '8px', 
          overflow: 'hidden',
          aspectRatio: isMobile ? '16/9' : 'auto',
          backgroundColor: '#000' // Fallback background
        }}>
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%', 
            backgroundColor: 'rgba(0, 0, 0, 0.5)', 
            zIndex: 1, 
            borderRadius: '8px', 
            pointerEvents: 'none' 
          }}></div>
          
          {videoLoading && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 3,
              color: 'white',
              fontSize: isMobile ? '0.9rem' : '1rem',
              fontFamily: 'JetBrains Mono, monospace'
            }}>
              Loading video...
            </div>
          )}
          
          <VideoPlayer
            src="/videos/sy3outboard.mp4"
            autoPlay
            muted
            loop
            style={{
              width: '100%',
              display: 'block',
              position: 'relative',
              zIndex: 0,
              objectFit: 'cover'
            }}
          />
          <div style={{ 
            position: 'absolute', 
            bottom: isMobile ? '12px' : '18px', 
            right: isMobile ? '12px' : '18px', 
            zIndex: 2, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'flex-end' 
          }}>
            <img 
              src={logo} 
              alt={t('sylentsLogoAlt')} 
              style={{ 
                width: isMobile ? '60px' : '80px', 
                height: 'auto', 
                filter: 'brightness(0) invert(1)', 
                marginBottom: isMobile ? '4px' : '6px' 
              }} 
            />
            <span style={{ 
              color: 'white', 
              fontFamily: 'JetBrains Mono, monospace', 
              fontSize: isMobile ? '0.9rem' : '1rem', 
              fontWeight: 'normal', 
              background: 'rgba(0,0,0,0.32)', 
              borderRadius: '4px', 
              padding: isMobile ? '2px 6px' : '2px 8px', 
              width: '75%',
              maxWidth: '75%',
              marginLeft: 'auto',
              display: 'block',
              textAlign: 'left'
            }}>
              {t('modelShowcaseTagline')}
            </span>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.gradientSubheadline}>{t('onePager.offerTitle')}</h2>
        <div className={styles.offerGrid}>
          <div className={styles.card}>
            <Zap className={styles.icon} size={32} />
            <p>{t('onePager.offer1')}</p>
          </div>
          <div className={styles.card}>
            <Wrench className={styles.icon} size={32} />
            <p>{t('onePager.offer2')}</p>
          </div>
          <div className={styles.card}>
            <Globe className={styles.icon} size={32} />
            <p>{t('onePager.offer3')}</p>
          </div>
          <div className={styles.card}>
            <Banknote className={styles.icon} size={32} />
            <p><strong>{t('onePager.offer4Title')}</strong><br/>{t('onePager.offer4Desc')}</p>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.gradientSubheadline}>{t('onePager.benefitsTitle')}</h2>
        <div className={styles.benefitsGrid}>
          <div className={styles.benefitCard}>
            <Receipt className={styles.icon} size={32} />
            <h3>{t('onePager.benefit1Title')}</h3>
            <p>{t('onePager.benefit1Desc')}</p>
          </div>
          <div className={styles.benefitCard}>
            <Briefcase className={styles.icon} size={32} />
            <h3>{t('onePager.benefit2Title')}</h3>
            <p>{t('onePager.benefit2Desc')}</p>
          </div>
          <div className={styles.benefitCard}>
            <TrendingUp className={styles.icon} size={32} />
            <h3>{t('onePager.benefit3Title')}</h3>
            <p>{t('onePager.benefit3Desc')}</p>
          </div>
          <div className={styles.benefitCard}>
            <Rocket className={styles.icon} size={32} />
            <h3>{t('onePager.benefit4Title')}</h3>
            <p>{t('onePager.benefit4Desc')}</p>
          </div>
        </div>
      </section>

      <section className={styles.ctaSection}>
        <h2>{t('onePager.ctaTitle')}</h2>
        <div className={styles.kpiRow}>
          <div className={styles.kpi}>{t('onePager.spotsRemaining', 'still 140/159 Spots free')}</div>
          <div className={styles.progressBarWrap}>
            <div className={styles.progressBarLabel}>{t('progressBarLabel', '€110,000 / €250,000')}</div>
            <div className={styles.progressBar}>
              <div className={styles.progressBarFill} style={{ width: `${(110000/250000)*100}%` }}></div>
            </div>
            <div className={styles.progressBarAmount}>{t('progressBarAmount', '€110,000 raised')}</div>
          </div>
        </div>
        <div style={{marginBottom: '1.2rem', color: '#b6faff', fontFamily: 'JetBrains Mono, monospace', fontSize: '1rem', textAlign: 'center'}}>
          {t('onePager.ctaLegalNotice')}
        </div>
        <a href="https://www.beyondpropellers.com" className={styles.ctaLink} target="_blank" rel="noopener noreferrer">
          {t('onePager.ctaLink')}
        </a>
        <a
          href="/#contact"
          className={styles.ctaRegisterBtn}
        >
          {t('onePager.ctaRegisterButton')}
        </a>
      </section>

      <section className={styles.teamSection}>
        <h2 className={styles.teamSectionTitle}>{t('teamTitle')}</h2>
        <div className={styles.teamGrid}>
          <div className={styles.teamCard}>
            <img className={styles.teamAvatar} src="https://randomuser.me/api/portraits/men/32.jpg" alt={t('teamHansName')} />
            <div className={styles.teamName}>{t('teamHansName')}</div>
            <div className={styles.teamRole}>{t('teamHansRole')}</div>
            <a className={styles.teamLinkedInBtn} href="#" target="_blank" rel="noopener noreferrer">
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{marginRight: 4}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8a6 6 0 016 6v5a2 2 0 01-2 2H4a2 2 0 01-2-2v-5a6 6 0 016-6m0 0V6a4 4 0 118 0v2"></path></svg>
              {t('teamLinkedIn')}
            </a>
          </div>
          <div className={styles.teamCard}>
            <img className={styles.teamAvatar} src="https://randomuser.me/api/portraits/men/33.jpg" alt={t('teamHilmarName')} />
            <div className={styles.teamName}>{t('teamHilmarName')}</div>
            <div className={styles.teamRole}>{t('teamHilmarRole')}</div>
            <a className={styles.teamLinkedInBtn} href="#" target="_blank" rel="noopener noreferrer">
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{marginRight: 4}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8a6 6 0 016 6v5a2 2 0 01-2 2H4a2 2 0 01-2-2v-5a6 6 0 016-6m0 0V6a4 4 0 118 0v2"></path></svg>
              {t('teamLinkedIn')}
            </a>
          </div>
          <div className={styles.teamCard}>
            <img className={styles.teamAvatar} src="https://randomuser.me/api/portraits/men/34.jpg" alt={t('teamMarkusName')} />
            <div className={styles.teamName}>{t('teamMarkusName')}</div>
            <div className={styles.teamRole}>{t('teamMarkusRole')}</div>
            <a className={styles.teamLinkedInBtn} href="#" target="_blank" rel="noopener noreferrer">
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{marginRight: 4}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8a6 6 0 016 6v5a2 2 0 01-2 2H4a2 2 0 01-2-2v-5a6 6 0 016-6m0 0V6a4 4 0 118 0v2"></path></svg>
              {t('teamLinkedIn')}
            </a>
          </div>
        </div>
      </section>

      <footer className={styles.onePagerFooter}>
        <div className={styles.footerLogo}>
          <span className={styles.sylentsLogo}>syl<span>e</span>nts</span>
        </div>
        <div className={styles.footerContent}>
          <div className={styles.footerLinks}>
            <a href="/imprint" className={styles.footerLink}>{t('footerImprint')}</a>
            <span className={styles.footerSeparator}>•</span>
            <a href="/privacy" className={styles.footerLink}>{t('footerPrivacy')}</a>
          </div>
          <div className={styles.footerSocial}>
            <a href="https://twitter.com/beyondpropellers" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
              𝕏 Twitter
            </a>
            <span className={styles.footerSeparator}>•</span>
            <a href="https://t.me/beyondpropellers" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
              📱 Telegram
            </a>
          </div>
        </div>
      </footer>
      <FAQModal isOpen={isFAQModalOpen} onClose={() => setIsFAQModalOpen(false)} />
    </div>
  );
};

export default OnePager; 